import { booleanContains, centerOfMass, transformRotate } from "@turf/turf";

import { geoJSONUtil, scalePlot, unscalePlot } from "./utils";

import { DYNAMIC_FEATURE_ID_FIELD_NAME } from "./constants";

export const PARCELLAIRE_REDUCER_ACTIONS = Object.freeze({
    CLEAR_ALL_GEOJSON: "clearAllGeoJson",
    CHECKPOINT: "checkpoint",
    GO_TO_CHECKPOINT: "goToCheckpoint",
    PM_EDIT: "pmEdit",
    METADATA: "metadata",
    GEOJSON: "geoJson",
    SELECT: "select",
    LOCK: "lock",
    UNLOCK: "unlock",
    DELETE: "delete",
    CLEAR_DELETE: "clearDelete",
    ADD_PROPERTY: "addProperty",
    ADD_PROPERTY_WITH_PLOT_ID_REGENERATION: "addPropertyWithPlotIdRegeneration",
    SELECT_ALL: "selectAll",
    ROTATE: "rotate",
    RESIZE: "resize",
    RESET_SCALE: "resetScale",
    CLEAR_SCALE_MAP: "clearScaleMap",
});

export const initialParcellaireState = {
    initialGeoJsonData: null,
    checkpointGeoJsonData: null,
    transformedGeoJson: null,
    transformModeVectors: [],
    selectedLayers: [],
    lockedLayers: [],
    metadata: null,
    featuresToDelete: [],
    loadedMissionUuid: null,
    scaleMap: {},
};

export function parcellaireReducer(state, action) {
    const currentGeoJson = state.transformedGeoJson;

    switch (action.type) {
        case PARCELLAIRE_REDUCER_ACTIONS.CLEAR_ALL_GEOJSON:
            return {
                ...state,
                initialGeoJsonData: null,
                checkpointGeoJsonData: null,
                transformedGeoJson: null,
                transformModeVectors: [],
                selectedLayers: [],
                lockedLayers: [],
                featuresToDelete: [],
                loadedMissionUuid: null,
                scaleMap: {},
            };
        case PARCELLAIRE_REDUCER_ACTIONS.CHECKPOINT:
            return {
                ...state,
                checkpointGeoJsonData: currentGeoJson,
                transformModeVectors: [],
                selectedLayers: [],
            };
        case PARCELLAIRE_REDUCER_ACTIONS.GO_TO_CHECKPOINT:
            return {
                ...state,
                selectedLayers: [],
                transformModeVectors: [],
                transformedGeoJson: state.checkpointGeoJsonData,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.PM_EDIT: {
            return action.transform;
        }
        case PARCELLAIRE_REDUCER_ACTIONS.METADATA:
            // metadata is required
            return {
                ...state,
                metadata: action.metadata,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.GEOJSON:
            // geoJson, uuid is required
            return {
                ...state,
                transformModeVectors: [],
                selectedLayers: [],
                lockedLayers: [],
                featuresToDelete: [],
                loadedMissionUuid: action.uuid,
                initialGeoJsonData: action.geoJson,
                checkpointGeoJsonData: action.geoJson,
                transformedGeoJson: action.geoJson,
                scaleMap: {},
            };
        case PARCELLAIRE_REDUCER_ACTIONS.SELECT:
            // e, geoJsonSelect, allLayers is required
            return {
                ...state,
                selectedLayers: action.allLayers.filter(
                    (layer) =>
                        layer !== action.e.layer &&
                        layer.feature &&
                        booleanContains(
                            action.geoJsonSelect,
                            layer.toGeoJSON()
                        ) &&
                        !state.lockedLayers.find(
                            (lockedLayer) =>
                                geoJSONUtil.getIdOfFeature(layer.feature) ===
                                geoJSONUtil.getIdOfFeature(lockedLayer.feature)
                        )
                ),
                transformModeVectors: [],
                checkpointGeoJsonData: currentGeoJson,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.SELECT_ALL:
            // action.allLayers is required
            return {
                ...state,
                selectedLayers: action.allLayers.filter(
                    (layer) =>
                        layer.feature &&
                        !state.lockedLayers.find(
                            (lockedLayer) =>
                                geoJSONUtil.getIdOfFeature(layer.feature) ===
                                geoJSONUtil.getIdOfFeature(lockedLayer.feature)
                        )
                ),
                transformModeVectors: [],
                checkpointGeoJsonData: currentGeoJson,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.LOCK:
            return {
                ...state,
                selectedLayers: [],
                lockedLayers: [...state.lockedLayers, ...state.selectedLayers],
                transformModeVectors: [],
                checkpointGeoJsonData: currentGeoJson,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.UNLOCK:
            return {
                ...state,
                selectedLayers: [],
                lockedLayers: [],
                transformModeVectors: [],
                checkpointGeoJsonData: currentGeoJson,
            };
        case PARCELLAIRE_REDUCER_ACTIONS.DELETE:
            if (state.selectedLayers.length) {
                const deletedIds = new Set(
                    state.selectedLayers.map(
                        (layer) =>
                            layer.feature.properties[
                                DYNAMIC_FEATURE_ID_FIELD_NAME
                            ]
                    )
                );
                const finalGeoJsonData = {
                    ...currentGeoJson,
                    features: currentGeoJson.features.filter(
                        (feature) =>
                            !deletedIds.has(
                                feature.properties[
                                    DYNAMIC_FEATURE_ID_FIELD_NAME
                                ]
                            )
                    ),
                };
                const scaleMapAfterDelete = { ...state.scaleMap };
                deletedIds.forEach((id) => delete scaleMapAfterDelete[id]);
                return {
                    ...state,
                    featuresToDelete: state.featuresToDelete.concat(
                        state.selectedLayers
                            .filter((layer) => layer.feature.uuid)
                            .map((layer) => layer.feature.uuid)
                    ),
                    scaleMap: scaleMapAfterDelete,
                    transformModeVectors: [],
                    selectedLayers: [],
                    checkpointGeoJsonData: finalGeoJsonData,
                    transformedGeoJson: finalGeoJsonData,
                };
            } else return state;
        case PARCELLAIRE_REDUCER_ACTIONS.CLEAR_DELETE:
            return {
                ...state,
                featuresToDelete: [],
            };
        case PARCELLAIRE_REDUCER_ACTIONS.CLEAR_SCALE_MAP:
            return {
                ...state,
                scaleMap: {},
            };
        case PARCELLAIRE_REDUCER_ACTIONS.ADD_PROPERTY:
            if (
                state.selectedLayers.length &&
                action.propertyKey &&
                action.propertyValue
            ) {
                const updatedGeoJsonData = {
                    ...currentGeoJson,
                    features: currentGeoJson.features.map((feature) => {
                        const isSelected = state.selectedLayers.some(
                            (layer) =>
                                layer.feature.properties[
                                    DYNAMIC_FEATURE_ID_FIELD_NAME
                                ] ===
                                feature.properties[
                                    DYNAMIC_FEATURE_ID_FIELD_NAME
                                ]
                        );
                        if (isSelected) {
                            return {
                                ...feature,
                                properties: {
                                    ...feature.properties,
                                    [action.propertyKey]: action.propertyValue,
                                },
                            };
                        }
                        return feature;
                    }),
                };
                return {
                    ...state,
                    transformModeVectors: [],
                    selectedLayers: [],
                    checkpointGeoJsonData: updatedGeoJsonData,
                    transformedGeoJson: updatedGeoJsonData,
                };
            } else return state;
        case PARCELLAIRE_REDUCER_ACTIONS.ADD_PROPERTY_WITH_PLOT_ID_REGENERATION:
            if (
                state.selectedLayers.length &&
                action.propertyKey &&
                action.propertyValue
            ) {
                const featuresToDelete = [];
                const updatedGeoJsonData = {
                    ...currentGeoJson,
                    features: currentGeoJson.features.map((feature) => {
                        const isSelected = state.selectedLayers.some(
                            (layer) =>
                                layer.feature.properties[
                                    DYNAMIC_FEATURE_ID_FIELD_NAME
                                ] ===
                                feature.properties[
                                    DYNAMIC_FEATURE_ID_FIELD_NAME
                                ]
                        );
                        if (isSelected) {
                            const updatedProperties = {
                                ...feature.properties,
                                [action.propertyKey]: action.propertyValue,
                            };

                            if (feature.uuid) {
                                if (
                                    updatedProperties.x ||
                                    updatedProperties.y
                                ) {
                                    if (!updatedProperties.client_id) {
                                        updatedProperties.client_id =
                                            feature.properties.plot_id;
                                    }
                                    const plotId =
                                        feature.properties.plot_id.split("_");
                                    plotId[plotId.length - 2] =
                                        `X${updatedProperties.x ?? feature.properties.x}`;
                                    plotId[plotId.length - 1] =
                                        `Y${updatedProperties.y ?? feature.properties.y}`;
                                    updatedProperties.plot_id =
                                        plotId.join("_");
                                }
                                if (
                                    updatedProperties.x ||
                                    updatedProperties.y ||
                                    updatedProperties.experiment
                                ) {
                                    // we delete old mission feature because the backend will rely on compositekey for unicity, not on uuid
                                    featuresToDelete.push(feature.uuid);
                                }
                            } else {
                                // Regénérer le plot_id si on a experiment, x et y
                                if (
                                    updatedProperties.experiment &&
                                    updatedProperties.x &&
                                    updatedProperties.y
                                ) {
                                    updatedProperties.plot_id = `${action.slugify(
                                        updatedProperties.experiment,
                                        {
                                            replacement: "_",
                                            strict: true,
                                            lower: true,
                                        }
                                    )}_X${updatedProperties.x}_Y${updatedProperties.y}`;
                                }
                            }

                            return {
                                ...feature,
                                properties: updatedProperties,
                            };
                        }
                        return feature;
                    }),
                };
                return {
                    ...state,
                    featuresToDelete:
                        state.featuresToDelete.concat(featuresToDelete),
                    transformModeVectors: [],
                    selectedLayers: [],
                    checkpointGeoJsonData: updatedGeoJsonData,
                    transformedGeoJson: updatedGeoJsonData,
                };
            } else return state;
        case PARCELLAIRE_REDUCER_ACTIONS.ROTATE: {
            // Same rotation for every plot, checkpoint included, so pending
            // vectors keep it
            const rotatedIds = new Set(action.coRotateIds ?? []);
            rotatedIds.add(action.featureId);
            const updateFeatures = (geoJson) => ({
                ...geoJson,
                features: geoJson.features.map((feature) => {
                    const fid =
                        feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME];
                    if (rotatedIds.has(fid)) {
                        // Area centroid, like geoman, so the plot doesn't jump
                        const rotated = transformRotate(feature, action.angle, {
                            pivot: centerOfMass(feature),
                        });
                        return { ...feature, geometry: rotated.geometry };
                    }
                    return feature;
                }),
            });
            return {
                ...state,
                checkpointGeoJsonData: updateFeatures(
                    state.checkpointGeoJsonData
                ),
                transformedGeoJson: updateFeatures(currentGeoJson),
            };
        }
        case PARCELLAIRE_REDUCER_ACTIONS.RESIZE:
            if (
                state.selectedLayers.length &&
                action.scaleFactorWidth != null &&
                action.scaleFactorHeight != null
            ) {
                const widthFactor = action.scaleFactorWidth / 100;
                const heightFactor = action.scaleFactorHeight / 100;
                const newScaleMap = { ...state.scaleMap };
                const selectedIds = new Set(
                    state.selectedLayers.map(
                        (layer) =>
                            layer.feature.properties[
                                DYNAMIC_FEATURE_ID_FIELD_NAME
                            ]
                    )
                );
                const resizedGeoJsonData = {
                    ...currentGeoJson,
                    features: currentGeoJson.features.map((feature) => {
                        const featureId =
                            feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME];
                        const isSelected =
                            selectedIds.has(featureId) &&
                            feature.geometry.type === "Polygon";
                        if (isSelected) {
                            const { feature: scaled, scale } = scalePlot(
                                feature,
                                newScaleMap[featureId],
                                widthFactor,
                                heightFactor
                            );
                            newScaleMap[featureId] = scale;
                            return scaled;
                        }
                        return feature;
                    }),
                };
                return {
                    ...state,
                    scaleMap: newScaleMap,
                    transformModeVectors: [],
                    selectedLayers: [],
                    checkpointGeoJsonData: resizedGeoJsonData,
                    transformedGeoJson: resizedGeoJsonData,
                };
            } else return state;
        case PARCELLAIRE_REDUCER_ACTIONS.RESET_SCALE:
            if (state.selectedLayers.length) {
                const newScaleMapReset = { ...state.scaleMap };
                const selectedIdsForReset = new Set(
                    state.selectedLayers.map(
                        (layer) =>
                            layer.feature.properties[
                                DYNAMIC_FEATURE_ID_FIELD_NAME
                            ]
                    )
                );
                const resetGeoJsonData = {
                    ...currentGeoJson,
                    features: currentGeoJson.features.map((feature) => {
                        const featureId =
                            feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME];
                        const isSelected =
                            selectedIdsForReset.has(featureId) &&
                            feature.geometry.type === "Polygon";
                        if (isSelected && newScaleMapReset[featureId] != null) {
                            const resetFeature = unscalePlot(
                                feature,
                                newScaleMapReset[featureId]
                            );
                            delete newScaleMapReset[featureId];
                            return resetFeature;
                        }
                        return feature;
                    }),
                };
                return {
                    ...state,
                    scaleMap: newScaleMapReset,
                    transformModeVectors: [],
                    selectedLayers: [],
                    checkpointGeoJsonData: resetGeoJsonData,
                    transformedGeoJson: resetGeoJsonData,
                };
            } else return state;
        default:
            throw new Error("Unknown action: " + action.type);
    }
}
